import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/router';
import AppLink from '../links/AppLink';
import {
  ContentCard,
  Image,
  ContentCardCollectible,
  ContentCardCheckbox,
  Tooltip,
} from 'edcom-ui';
import './SearchResults.less';
import styles from './SearchResults.module.less';
import CollectionsContext from '../../services/CollectionsContext';
import useVariation from '../../services/useVariation';
import AccountContext from '../../services/AccountContext';

const resultsVariants = {
  exit: {
    transition: { staggerChildren: 0.02, staggerDirection: -1 },
  },
};
const cardVariants = {
  initial: {
    scale: 0.96,
    y: 30,
    opacity: 0,
  },
  enter: {
    scale: 1,
    y: 0,
    opacity: 1,
    transition: { duration: 0.5, ease: [0.48, 0.15, 0.25, 0.96] },
  },
  exit: {
    scale: 0.6,
    y: 100,
    opacity: 0,
    transition: { duration: 0.2, ease: [0.48, 0.15, 0.25, 0.96] },
  },
};

export default function SearchResults({
  results = [],
  selected,
  handleSelected,
  setShowOnlySelected,
}) {
  const router = useRouter();
  const { isCollected, collectContent } = useContext(CollectionsContext);

  const handleCollect = (type, id, model) => () => {
    collectContent(type, id, model);
  };

  const { isPremium } = useContext(AccountContext);

  const [variation, bucketUser] = useVariation('multi-select-srp');
  if (isPremium) {
    bucketUser();
  }

  const contentCards = results.map((contentModel, i) => {
    const key = `${contentModel.type}:${contentModel.id}`;
    const resultClasses = clsx(styles.result, contentModel.type, {
      'premium-only': contentModel.premium,
    });
    const isTooltipCard = variation === 1 && i === 1;

    let cta =
      variation === 1 ? (
        <ContentCardCheckbox
          checked={selected.indexOf(contentModel) !== -1}
          onChange={checked => {
            let newSelected = selected.slice();
            if (checked) {
              if (selected.length >= 50) {
                alert('Cannot select over 50!');
                return;
              }
              newSelected.push(contentModel);
              handleSelected([...selected, contentModel]);
            } else {
              for (let i = 0; i < newSelected.length; i++) {
                if (newSelected[i].id === contentModel.id) {
                  newSelected.splice(i, 1);
                }
              }
              handleSelected(newSelected);
            }
          }}
          onClick={e => {
            e.stopPropagation();
          }}
        />
      ) : (
        <ContentCardCollectible
          collected={isCollected(contentModel.type, contentModel.id)}
          onClick={handleCollect(
            contentModel.type,
            contentModel.id,
            contentModel
          )}
        />
      );

    let cardClassName = selected.indexOf(contentModel) === -1 ? '' : 'selected';

    return (
      <motion.div
        key={key}
        whileTap={{ scale: 0.95 }}
        variants={cardVariants}
        className={resultClasses}
      >
        <div
          className={isTooltipCard ? styles.contentCardWrapperForTooltip : ''}
        >
          {isTooltipCard && (
            <Tooltip
              text={
                'Click the checkbox for the options to print and add to Assignments and Collections.'
              }
              localStorageKey={'haveShownSelectTooltip'}
              hideAfterInMilliseconds={9500}
              style={{
                container: 'ttContainer',
                box: 'ttBox',
                triangle: 'ttTriangle',
              }}
            />
          )}
          <AppLink href={contentModel.url}>
            <ContentCard
              id={contentModel.id}
              type={contentModel.type}
              title={contentModel.title}
              description={contentModel.description}
              href={contentModel.url}
              grade={contentModel.grade}
              subject={contentModel.subject}
              isPremium={contentModel.premium}
              isEL={contentModel.el}
              isNew={contentModel.new}
              // TODO: get isAdmin property from contentModel
              isAdminOnly={false}
              withinGrid={true}
              className={cardClassName}
            >
              {contentModel.thumbnail && <Image src={contentModel.thumbnail} />}
              {cta}
            </ContentCard>
          </AppLink>
        </div>
      </motion.div>
    );
  });

  if (!results.length) {
    return (
      <>
        <h2>Sorry, no results match your search.</h2>
        You can <AppLink href="/resources/">remove all filters</AppLink> and try
        again.
      </>
    );
  }

  let viewAll =
    selected === results ? (
      <div
        className={styles.viewAll}
        onClick={() => {
          setShowOnlySelected(false);
        }}
      >
        Back to View All
      </div>
    ) : (
      ''
    );

  return (
    <AnimatePresence exitBeforeEnter initial={false}>
      <motion.div
        key={router.asPath}
        className={styles.list}
        initial="initial"
        animate="enter"
        exit="exit"
        variants={resultsVariants}
      >
        {viewAll}
        {contentCards}
      </motion.div>
    </AnimatePresence>
  );
}

const contentCardPropTypes = {
  type: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  thumbnail: PropTypes.string,
  thumbnailWidth: PropTypes.number,
  subject: PropTypes.string.isRequired,
  grade: PropTypes.string.isRequired,
  rating: PropTypes.number,
  new: PropTypes.bool.isRequired,
  answersheet: PropTypes.bool.isRequired,
  premium: PropTypes.bool,
  el: PropTypes.bool.isRequired,
};

SearchResults.propTypes = {
  results: PropTypes.arrayOf(PropTypes.shape(contentCardPropTypes)),
  selected: PropTypes.array,
  handleSelected: PropTypes.func,
  setShowOnlySelected: PropTypes.func,
};
